import { Button, Input, MessageSquare, Send, X } from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { feedbackApi } from "../../../services/feedback.api";
import {
  FeedbackPriority,
  FeedbackType,
} from "../../../types/feedback.definition";

interface FeedbackFormProps {
  managerId: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  managerId,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: FeedbackType.GENERAL_FEEDBACK,
    subject: "",
    message: "",
    priority: FeedbackPriority.MEDIUM,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // MED-1653: like the other forms, the button stays disabled until the form is valid.
  const isFormValid =
    formData.subject.trim() !== "" && formData.message.trim().length >= 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.subject.trim()) {
      setError(t("helpSupport.feedback.form.errors.subjectRequired"));
      return;
    }
    if (!formData.message.trim()) {
      setError(t("helpSupport.feedback.form.errors.messageRequired"));
      return;
    }
    if (formData.message.trim().length < 10) {
      setError(t("helpSupport.feedback.form.errors.messageMinLength"));
      return;
    }

    setIsSubmitting(true);

    try {
      await feedbackApi.createFeedback({
        managerId,
        type: formData.type,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        priority: formData.priority,
      });

      setSuccess(true);
      // Reset form
      setFormData({
        type: FeedbackType.GENERAL_FEEDBACK,
        subject: "",
        message: "",
        priority: FeedbackPriority.MEDIUM,
      });

      // Call success callback after a delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 2000);
    } catch (err) {
      console.error("Error submitting feedback:", err);
      setError(t("helpSupport.feedback.form.errors.submitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="p-6 rounded-lg border bg-success-50 border-success-200">
        <div className="flex items-center space-x-3">
          <div className="flex justify-center items-center w-10 h-10 rounded-full bg-success-100">
            <MessageSquare className="w-5 h-5 text-success-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-success-900">
              {t("helpSupport.feedback.form.success.title")}
            </h3>
            <p className="mt-1 text-sm text-success-700">
              {t("helpSupport.feedback.form.success.message")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Feedback Type */}
      <div>
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          {t("helpSupport.feedback.form.labels.feedbackType")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <select
          value={formData.type}
          onChange={(e) =>
            setFormData({ ...formData, type: e.target.value as FeedbackType })
          }
          className="px-3 py-2 w-full rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
        >
          <option value={FeedbackType.GENERAL_FEEDBACK}>
            {t("helpSupport.feedback.form.types.generalFeedback")}
          </option>
          <option value={FeedbackType.BUG}>
            {t("helpSupport.feedback.form.types.bugReport")}
          </option>
          <option value={FeedbackType.FEATURE_REQUEST}>
            {t("helpSupport.feedback.form.types.featureRequest")}
          </option>
          <option value={FeedbackType.SUPPORT_REQUEST}>
            {t("helpSupport.feedback.form.types.supportRequest")}
          </option>
        </select>
      </div>

      {/* Priority */}
      <div>
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          {t("helpSupport.feedback.form.labels.priority")}
        </label>
        <select
          value={formData.priority}
          onChange={(e) =>
            setFormData({
              ...formData,
              priority: e.target.value as FeedbackPriority,
            })
          }
          className="px-3 py-2 w-full rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value={FeedbackPriority.LOW}>
            {t("helpSupport.feedback.form.priority.low")}
          </option>
          <option value={FeedbackPriority.MEDIUM}>
            {t("helpSupport.feedback.form.priority.medium")}
          </option>
          <option value={FeedbackPriority.HIGH}>
            {t("helpSupport.feedback.form.priority.high")}
          </option>
        </select>
      </div>

      {/* Subject */}
      <div>
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          {t("helpSupport.feedback.form.labels.subject")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <Input
          type="text"
          value={formData.subject}
          onChange={(e) =>
            setFormData({ ...formData, subject: e.target.value })
          }
          placeholder={t("helpSupport.feedback.form.placeholders.subject")}
          className="w-full"
          required
        />
      </div>

      {/* Message */}
      <div>
        <label className="block mb-2 text-sm font-medium text-neutral-700">
          {t("helpSupport.feedback.form.labels.message")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.message}
          onChange={(e) =>
            setFormData({ ...formData, message: e.target.value })
          }
          placeholder={t("helpSupport.feedback.form.placeholders.message")}
          rows={6}
          className="px-3 py-2 w-full rounded-lg border resize-none border-neutral-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          required
          minLength={10}
        />
        <p className="mt-1 text-xs text-neutral-500">
          {t("helpSupport.feedback.form.charactersCount", {
            count: formData.message.length,
            min: 10,
          })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end pt-4 space-x-3">
        {onCancel && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <X className="mr-2 w-4 h-4" />
            {t("helpSupport.feedback.form.cancel")}
          </Button>
        )}
        <Button type="submit" disabled={isSubmitting || !isFormValid}>
          {isSubmitting ? (
            <>
              <div className="mr-2 w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
              {t("helpSupport.feedback.form.submitting")}
            </>
          ) : (
            <>
              <Send className="mr-2 w-4 h-4" />
              {t("helpSupport.feedback.form.submit")}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
